import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';
import { NotFound } from '../ui/States';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: Root,
  notFoundComponent: NotFound,
});

/** Longest wait for a page that is still loading its data to show its heading. */
const HEADING_WAIT_MS = 3000;

function focusHeading(): boolean {
  const h1 = document.querySelector<HTMLElement>('main h1');
  if (!h1) return false;
  h1.tabIndex = -1;
  h1.focus({ preventScroll: false });
  return true;
}

/**
 * On navigation, move focus to the new page's heading and announce it (screen readers, keyboard users).
 * Runs once the new route has rendered, not when the URL changes: by then the old page's h1 is gone.
 * A page still suspended on its data gets focus when its heading appears.
 */
function RouteFocus() {
  const router = useRouter();
  useEffect(
    () =>
      router.subscribe('onRendered', ({ fromLocation, pathChanged }) => {
        // The first load keeps the browser's own focus; hash changes stay on the same page.
        if (!fromLocation || !pathChanged || focusHeading()) return;
        const observer = new MutationObserver(() => {
          if (focusHeading()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => {
          observer.disconnect();
        }, HEADING_WAIT_MS);
      }),
    [router],
  );
  return null;
}

function Root() {
  return (
    <>
      <RouteFocus />
      <Outlet />
    </>
  );
}
